import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Building2, KeyRound, Layers3, Loader2, Save, ShieldCheck, Users } from "lucide-react";
import { toast } from "sonner";

export default function Administracao() {
  const { tenant, refresh } = useAuth();
  const [available, setAvailable] = useState([]);
  const [modules, setModules] = useState({});
  const [initial, setInitial] = useState({});
  const [emitentes, setEmitentes] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem("up2_admin_access") === "ok");
  const [accessCode, setAccessCode] = useState("");
  const [accessing, setAccessing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingEmitente, setSavingEmitente] = useState(null);

  const load = useCallback(async () => {
    if (!unlocked) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [{ data: moduleData }, { data: emitentesData }, { data: bancosData }] = await Promise.all([
        api.get("/tenant/modules"),
        api.get("/emitentes/pix-config"),
        api.get("/bancos"),
      ]);
      setAvailable(moduleData.available || []);
      setModules(moduleData.modules || {});
      setInitial(moduleData.modules || {});
      setEmitentes(emitentesData || []);
      setBancos(bancosData || []);
    } catch (err) {
      toast.error("Não foi possível carregar a administração");
    } finally {
      setLoading(false);
    }
  }, [unlocked]);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = useMemo(
    () => JSON.stringify(modules) !== JSON.stringify(initial),
    [modules, initial],
  );

  const toggle = (key) => (enabled) => {
    setModules((current) => ({ ...current, [key]: enabled }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/tenant/modules", { modules });
      setModules(data.modules || {});
      setInitial(data.modules || {});
      await refresh();
      toast.success("Configuração de módulos atualizada");
    } catch (err) {
      toast.error("Erro ao salvar módulos");
    } finally {
      setSaving(false);
    }
  };

  const unlock = async (e) => {
    e.preventDefault();
    setAccessing(true);
    try {
      await api.post("/tenant/admin-access", { code: accessCode });
      sessionStorage.setItem("up2_admin_access", "ok");
      setUnlocked(true);
      toast.success("Acesso administrativo liberado");
    } catch (err) {
      toast.error("Código de administração inválido");
    } finally {
      setAccessing(false);
    }
  };

  const toggleEmitenteBank = async (emitente, bancoId, enabled) => {
    const current = emitente.pix_bank_ids || [];
    const next = enabled
      ? Array.from(new Set([...current, bancoId]))
      : current.filter((id) => id !== bancoId);

    setEmitentes((items) => items.map((item) => (
      item.emitente_key === emitente.emitente_key ? { ...item, pix_bank_ids: next } : item
    )));
    setSavingEmitente(emitente.emitente_key);
    try {
      const { data } = await api.put(`/emitentes/${emitente.emitente_key}/pix-bancos`, { pix_bank_ids: next });
      setEmitentes((items) => items.map((item) => (item.emitente_key === emitente.emitente_key ? data : item)));
      toast.success("Controle Pix do emitente atualizado");
    } catch (err) {
      toast.error("Erro ao atualizar bancos Pix do emitente");
      load();
    } finally {
      setSavingEmitente(null);
    }
  };

  const activeBancos = bancos.filter((b) => b.ativo !== false);

  if (!unlocked) {
    return (
      <div className="min-h-[60vh] grid place-items-center" data-testid="administracao-lock">
        <Card className="w-full max-w-md rounded-sm border border-zinc-200 shadow-none">
          <form onSubmit={unlock} className="p-6 space-y-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-sm border border-zinc-200 grid place-content-center">
                <KeyRound className="w-4 h-4 text-orange-600" />
              </div>
              <div>
                <div className="font-display font-semibold">Acesso administrativo</div>
                <p className="text-xs text-zinc-500 mt-0.5">Informe o código de acesso da Administração.</p>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">Código</Label>
              <Input
                type="password"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                className="rounded-sm h-10 font-mono text-sm"
                autoFocus
                data-testid="administracao-access-code"
              />
            </div>
            <Button type="submit" className="w-full rounded-sm" disabled={accessing || !accessCode.trim()}>
              {accessing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
              Liberar acesso
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="administracao-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
            Tenant
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">
            Administração
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {tenant?.empresa || "Cliente"} · módulos e Pix por emitente.
          </p>
        </div>
        <Button
          type="button"
          className="rounded-sm"
          onClick={save}
          disabled={!dirty || saving || loading}
          data-testid="administracao-save"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Salvar
        </Button>
      </div>

      <Card className="rounded-sm border border-zinc-200 shadow-none">
        <div className="p-4 border-b border-zinc-200 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <ShieldCheck className="w-4 h-4 text-orange-600 shrink-0" />
            <div className="font-display font-semibold truncate">Configuração de módulos</div>
          </div>
          <Badge variant="outline" className="rounded-sm font-mono text-[10px] uppercase">
            {available.filter((m) => modules[m.key]).length}/{available.length} ativos
          </Badge>
        </div>

        {loading ? (
          <div className="p-8 text-sm text-zinc-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Carregando módulos...
          </div>
        ) : (
          <div className="divide-y divide-zinc-200">
            {available.map((mod) => {
              const enabled = Boolean(modules[mod.key]);
              return (
                <div
                  key={mod.key}
                  className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                  data-testid={`module-row-${mod.key}`}
                >
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="h-9 w-9 rounded-sm border border-zinc-200 grid place-content-center shrink-0">
                      <Layers3 className="w-4 h-4 text-zinc-500" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="font-semibold text-sm">{mod.name}</h2>
                        <Badge
                          variant="outline"
                          className={`rounded-sm text-[10px] uppercase font-mono ${
                            enabled ? "border-emerald-200 text-emerald-700 bg-emerald-50" : "border-zinc-200 text-zinc-500"
                          }`}
                        >
                          {enabled ? "Ativo" : "Inativo"}
                        </Badge>
                      </div>
                      <p className="text-sm text-zinc-500 mt-1">{mod.description}</p>
                      <div className="text-[11px] font-mono text-zinc-400 mt-2">{mod.key}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 sm:justify-end">
                    <Label
                      htmlFor={`module-${mod.key}`}
                      className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold"
                    >
                      Habilitar
                    </Label>
                    <Switch
                      id={`module-${mod.key}`}
                      checked={enabled}
                      onCheckedChange={toggle(mod.key)}
                      data-testid={`module-switch-${mod.key}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Card className="rounded-sm border border-zinc-200 shadow-none" data-testid="administracao-pix-bancos">
        <div className="p-4 border-b border-zinc-200 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Users className="w-4 h-4 text-orange-600 shrink-0" />
            <div>
              <div className="font-display font-semibold">Pix por emitente e banco</div>
              <p className="text-xs text-zinc-500 mt-0.5">
                Habilite quais bancos podem monitorar movimentações Pix para cada emitente de boleto.
              </p>
            </div>
          </div>
          <Badge variant="outline" className="rounded-sm font-mono text-[10px] uppercase">
            {emitentes.length} emitentes · {activeBancos.length} bancos
          </Badge>
        </div>

        {loading ? (
          <div className="p-8 text-sm text-zinc-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Carregando emitentes e bancos...
          </div>
        ) : emitentes.length === 0 || activeBancos.length === 0 ? (
          <div className="p-8 text-sm text-zinc-500" data-testid="administracao-pix-empty">
            Cadastre ao menos um emitente de boleto e um banco ativo para liberar o monitoramento Pix.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl" data-testid="administracao-pix-table">
              <thead>
                <tr>
                  <th>Emitente</th>
                  {activeBancos.map((banco) => (
                    <th key={banco.id} className="text-center min-w-36">
                      <div className="inline-flex items-center justify-center gap-1.5">
                        <Building2 className="w-3.5 h-3.5 text-zinc-400" />
                        <span>{banco.bank_code}</span>
                      </div>
                      <div className="text-[10px] font-normal text-zinc-500 mt-1">{banco.banco_nome}</div>
                    </th>
                  ))}
                  <th className="text-right">Pix ativo</th>
                </tr>
              </thead>
              <tbody>
                {emitentes.map((emitente) => {
                  const selected = emitente.pix_bank_ids || [];
                  return (
                    <tr key={emitente.emitente_key} data-testid={`administracao-pix-emitente-${emitente.emitente_key}`}>
                      <td>
                        <div className="font-medium">{emitente.razao_social}</div>
                        <div className="text-[11px] font-mono text-zinc-500">{emitente.cnpj || "-"}</div>
                      </td>
                      {activeBancos.map((banco) => {
                        const checked = selected.includes(banco.id);
                        return (
                          <td key={banco.id} className="text-center">
                            <Checkbox
                              checked={checked}
                              disabled={savingEmitente === emitente.emitente_key}
                              onCheckedChange={(value) => toggleEmitenteBank(emitente, banco.id, Boolean(value))}
                              aria-label={`Pix ${emitente.razao_social} ${banco.banco_nome}`}
                              className="mx-auto"
                              data-testid={`administracao-pix-${emitente.emitente_key}-${banco.id}`}
                            />
                          </td>
                        );
                      })}
                      <td className="text-right">
                        <Badge
                          variant="outline"
                          className={`rounded-sm text-[10px] uppercase font-mono ${
                            selected.length ? "border-emerald-200 text-emerald-700 bg-emerald-50" : "border-zinc-200 text-zinc-500"
                          }`}
                        >
                            {savingEmitente === emitente.emitente_key ? "Salvando" : `${selected.length}/${activeBancos.length}`}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
